import { useCallback } from 'react'
import { useThemeContext } from '@/contexts/ThemeContext'
import { localStorageVariables } from '@/types/localStorageVariables'

/**
 * Hook to ensure proper API call sequencing
 * 
 * This matches Angular behavior where:
 * 1. getColorScheme (theme loading) is always the first API call
 * 2. Property information gets stored in localStorage 
 * 3. Other API calls wait for property information to be available
 * 
 * Usage in components:
 * ```
 * const { waitForThemeReady, isThemeReady } = useApiSequencing()
 * 
 * // Before making API calls:
 * await waitForThemeReady()
 * // Now safe to make other API calls
 * ```
 */
export function useApiSequencing() {
  const {
    isThemeReady,
    waitForThemeReady,
    propertyInfoStatus,
    getPropertyInformationStatus
  } = useThemeContext()

  /**
   * Execute a function only after theme is ready
   * This ensures proper API call sequencing
   */
  const executeAfterThemeReady = useCallback(async <T>(
    apiCall: () => Promise<T>
  ): Promise<T> => {
    console.log('🔄 Ensuring theme is ready before API call...')

    // Wait for theme to be ready (matches Angular BehaviorSubject behavior)
    await waitForThemeReady()

    console.log('✅ Theme is ready, proceeding with API call')

    // Execute the API call
    return await apiCall()
  }, [waitForThemeReady])

  /**
   * Check if property information is loaded (matches Angular getPropertyInformationStatus)
   */
  const isPropertyInfoLoaded = useCallback((): boolean => {
    return getPropertyInformationStatus()
  }, [getPropertyInformationStatus])

  /**
   * Get current site name from localStorage (commonly needed for API calls)
   */
  const getSiteName = useCallback((): string | null => {
    return localStorage.getItem('sitename')
  }, [])

  return {
    // Status checking
    isThemeReady: isThemeReady(),
    propertyInfoStatus,
    isPropertyInfoLoaded,

    // Execution helpers
    waitForThemeReady,
    executeAfterThemeReady,

    // Data getters
    getPropertyId,
    getSiteName
  }
}

/**
* Get current property ID from localStorage (commonly needed for API calls)
*/
export const getPropertyId = (): string => {
  return localStorage.getItem(localStorageVariables.PropertyId) || ''
}